<template>
	<div id="clientAISearch" class="jc-view" v-if="isAuth && authUser.permissions.includes('CLIENT_ACCESS')">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="AI Search" />
		<div class="redirections-list mt-3 d-flex justify-content-between align-items-center">
			<div>
				<span>
					View AI search analytics to gain insights into client interactions and improve service delivery.
				</span>
			</div>
		</div>
		<div class="row pt-4">
			<!-- Totals Cards -->
			<div class="row pt-2 w-100">
				<!-- Total Queries -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Total Queries</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										{{ formatNumber(search.totals.total_responses, 0) }}
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>

				<!-- Total Tokens -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Total Tokens</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										{{ formatNumber(search.totals.total_tokens, 0) }}
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>

				<!-- Total Cost -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Total Cost</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										${{ formatNumber(search.totals.total_cost, 4) }}
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="row pt-2">
				<!-- Average Tokens Per Query -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Average Tokens Per Query</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										{{ formatNumber(search.totals.avg_tokens_per_response, 4) }}
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>

				<!-- Average Cost Per Query -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Average Cost Per Query</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										${{ formatNumber(search.totals.avg_cost_per_response, 4) }}
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>

				<!-- Average Response time Per Query -->
				<div class="col-12 col-lg-4 col-md-6 mb-3">
					<div class="card shadow-sm border-0 rounded-lg">
						<div class="card-body d-flex align-items-center">
							<div class="icon-container bg-light rounded-circle d-flex justify-content-center align-items-center me-3"
								style="width: 48px; height: 48px;">
								<font-awesome-icon :icon="['far', 'chart-bar']" class="text-secondary fs-4" />
							</div>
							<div>
								<h6 class="text-muted m-0">Average Response Time</h6>
								<h3 class="fw-bold m-0">
									<template v-if="!loading">
										{{ formatNumber(search.totals.avg_response_time, 4) }} secs
									</template>
									<div v-else class="spinner-border spinner-border-sm" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</h3>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Top Queries Chart -->
			<div class="row pt-2">
				<div class="col-12 col-xl-12 col-lg-12 mb-3">
					<div class="card">
						<div class="card-header">
							<span>✉️ Top Queries</span>
						</div>
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center">
								<small style="color: #bbb" class="d-block">Top 10 search queries/responses chart</small>
							</div>
							<StatsLineChart v-if="!loading" :data="campaignsLineChart" :options="lineChartOptions"
								:height="100" class="mb-4" />
						</div>
					</div>
				</div>
			</div>

			<!-- Queries/Answers Table with Filters and Search -->
			<div class="row pt-2">
				<div class="col-12 col-lg-12 col-xl-12 col-md-12 mb-3">
					<!-- Increased card height -->
					<div class="card" style="min-height: calc(100vh * 0.85);">
						<div class="card-header bg-info d-flex justify-content-between align-items-center">
							<div class="card-header-title">⌛ Search Queries/Answers</div>
						</div>
						<div class="card-body d-flex flex-column">
							<!-- Row with Search on Left and Date Filters on Right -->
							<div class="d-flex justify-content-between align-items-center mb-3">
								<!-- Search Field on the left -->
								<div class="search-box" style="max-width: 250px;">
									<div class="input-group shadow-sm rounded">
										<span class="input-group-text bg-white">
											<font-awesome-icon :icon="['fas', 'search']" class="text-primary" />
										</span>
										<input type="text" class="form-control" placeholder="Search"
											v-model="searchTerm" @keyup.enter="applyFilters" />
									</div>
								</div>

								<!-- Date Filters on the right -->
								<div class="d-flex align-items-center" style="gap: 10px;">
									<span class="text-muted">Filter by Date:</span>

									<!-- Start Date -->
									<div class="input-group input-group-sm" style="width: 150px;">
										<input type="date" class="form-control" v-model="startDate" />
									</div>

									<!-- End Date -->
									<div class="input-group input-group-sm" style="width: 150px;">
										<input type="date" class="form-control" v-model="endDate" />
									</div>

									<!-- Search and Clear buttons -->
									<button class="btn btn-sm btn-primary text-white"
										@click="applyFilters">
										Filter <font-awesome-icon :icon="['fas', 'search']" />
									</button>

									<button v-if="params.search || params.start_date || params.end_date"
										@click="clearFilters" class="btn btn-sm btn-danger text-white">
										Clear <font-awesome-icon :icon="['fas', 'times']" />
									</button>
								</div>
							</div>

							<hr class="my-3" />
							<div class="tableFixHead flex-grow-1">
								<template v-if="!loading">
									<table v-if="responses.length > 0" class="table table-hover">
										<thead>
											<tr>
												<th>#</th>
												<th>Question</th>
												<th>Answer</th>
												<th>Tokens</th>
												<th>Cost</th>
												<th>Response Time</th>
											</tr>
										</thead>
										<tbody>
											<tr role="button" v-for="e in responses" :key="e.id" @click="showModal(e)">
												<td>{{ e.id }}</td>
												<td>{{ e.query ? e.query.query : '' }}</td>
												<td class="text-center">
													<div class="overflow-auto" style="max-height:100px;">
														<span v-html="e.answer"></span>
													</div>
												</td>
												<td>{{ (e.input_tokens + e.output_tokens) || 0 }}</td>
												<td>${{ (formatNumber(Number(e.input_cost) + Number(e.output_cost), 4))
													|| 0 }}</td>
												<td>{{ e.response_time }} secs</td>
											</tr>
										</tbody>
									</table>
									<div v-else class="text-center">
										<span class="fs-4">No results found</span>
									</div>
								</template>

								<div v-else class="text-center">
									<div class="spinner-border" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</div>
							</div>

							<!-- Pagination inside the card -->
							<div class="mt-3" v-if="!loading && totalItems > perPage">
								<Pagination :totalItems="totalItems" :currentPage="page" :perPage="perPage"
									@pageChanged="setPage" :goButton="false" styled="simple" borderActiveColor=""
									borderTextActiveColor="text-white" :float-right="true" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Modal for displaying selected response info -->
			<div class="modal fade" id="responseModal" tabindex="-1" aria-labelledby="responseModalLabel"
				aria-hidden="true">
				<div class="modal-dialog modal-lg modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title" id="responseModalLabel">Response Details</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body" v-if="selectedResponse">
							<!-- Question -->
							<div class="mb-3">
								<h6 class="fw-bold text-secondary mb-1">Question:</h6>
								<p class="mb-0">{{ selectedResponse.query && selectedResponse.query.query ?
									selectedResponse.query.query : 'N/A' }}</p>
							</div>

							<!-- Answer in a card -->
							<div class="mb-3">
								<h6 class="fw-bold text-secondary mb-1">Answer:</h6>
								<div class="card bg-light mb-3 border-0 rounded-lg">
									<div class="card-body p-2" style="max-height: 200px; overflow:auto;">
										<span v-html="toMarkdown(selectedResponse.answer)"></span>
									</div>
								</div>
							</div>

							<!-- Tokens and Cost -->
							<div class="row mb-3">
								<div class="col">
									<div class="card card-body p-2 shadow-sm border-1 rounded-lg">
										<h6 class="fw-bold text-secondary mb-1">Total Tokens</h6>
										<p class="mb-0">{{ (selectedResponse.input_tokens +
											selectedResponse.output_tokens) || 0 }}
										</p>
									</div>
								</div>
								<div class="col">
									<div class="card card-body p-2">
										<h6 class="fw-bold text-secondary mb-1">Total Cost</h6>
										<p class="mb-0">${{ (selectedResponse.input_cost + selectedResponse.output_cost)
											|| 0 }}</p>
									</div>
								</div>
							</div>

							<!-- Response Time and Cached -->
							<div class="row mb-3">
								<div class="col">
									<div class="card card-body p-2">
										<h6 class="fw-bold text-secondary mb-1">Response Time</h6>
										<p class="mb-0">{{ selectedResponse.response_time || 'N/A' }} secs</p>
									</div>
								</div>
								<div class="col d-flex align-items-center">
									<h6 class="fw-bold text-secondary mb-1 me-2">Cached:</h6>
									<span class="badge"
										:class="selectedResponse.cached === 1 ? 'bg-success' : 'bg-secondary'">
										{{ selectedResponse.cached === 1 ? 'Yes' : 'No' }}
									</span>
								</div>
							</div>

							<!-- Sources -->
							<div class="mb-3">
								<h6 class="fw-bold text-secondary mb-2">Sources:</h6>
								<ul class="list-group list-group-flush">
									<li v-for="(source, index) in selectedResponse.sources" :key="index"
										class="list-group-item p-2">
										<a :href="source.url" target="_blank" rel="noopener noreferrer"
											class="text-primary">
											{{ source.title }}
										</a>
									</li>
								</ul>
							</div>

							<!-- Related Queries -->
							<div>
								<h6 class="fw-bold text-secondary mb-2">Related Queries:</h6>
								<ul class="list-group list-group-flush">
									<li v-for="(item, idx) in selectedResponse.related" :key="idx"
										class="list-group-item p-2">
										{{ item }}
									</li>
								</ul>
							</div>
						</div>

						<div class="modal-footer bg-light">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						</div>
					</div>
				</div>
			</div>

		</div>
	</div>
</template>

<script>
import StatsLineChart from '@/components/charts/StatsLineChart.vue'
import Pagination from "@/components/juniper/pagination/Pagination"
import { mapGetters, mapState } from 'vuex'
import { differenceInDays } from 'date-fns'
import juniperApi from '@/api/juniperApi'
import axios from 'axios'
import { Modal } from 'bootstrap'
import { marked } from 'marked';

export default {
	name: 'ClientAISearch',
	components: {
		StatsLineChart,
		Pagination
	},
	data() {
		return {
			client: {},
			search: { totals: {} },
			loading: false,
			campaignsLineChart: {
				labels: [],
				datasets: [
					{
						label: 'Queries',
						backgroundColor: '#dedede',
						borderColor: '#2196f3',
						data: [],
					},
				],
			},
			responses: [],
			page: this.$route.query.page || 1,
			perPage: 10,
			totalItems: 0,
			lineChartOptions: {
				responsive: true,
				maintainAspectRatio: true,
				scales: {
					x: {
						grid: {
							display: false,
						},
						ticks: {
							callback: function (value) {
								const label = this.getLabelForValue(value);
								return label.length > 10 ? label.substring(0, 10) + '...' : label;
							}
						}
					},
				},
			},
			aiSearchAPIUrl: process.env.VUE_APP_AI_SEARCH_API_URL,
			params: {},
			searchTerm: '',
			startDate: '',
			endDate: '',
			selectedResponse: null,
			$responseModal: null
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
	},
	async created() {
		await this.getClient()
		await this.getStatistics()
		await this.getResponses()
	},
	mounted() {
		this.$responseModal = new Modal(document.getElementById('responseModal'), {
			backdrop: 'static',
			keyboard: true,
		})
	},
	methods: {
		toMarkdown(text) {
			return marked(text);
		},
		formatNumber(value, decimals = 4) {
			if (value == null || isNaN(value)) {
				return `0.${'0'.repeat(decimals)}`;
			}
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			}).format(value);
		},
		async getClient() {
			this.loading = true
			try {
				const { data } = await juniperApi.get('/clients/' + this.$route.params.id)
				this.client = data
			} catch (error) {
				console.log(error)
			} finally {
				this.loading = false
			}
			return;
		},

		async getStatistics() {
			this.loading = true
			try {
				const jurisdiction = this.client?.heygov_jurisdiction ?? this.client.url
				const { data } = await axios.get(`${this.aiSearchAPIUrl}search/analytics?jurisdiction=${jurisdiction}`, {})

				if (data.data) {
					this.search = data.data
					const top_queries = data.data.top_queries || []
					this.campaignsLineChart.labels = top_queries.map(query => query.query);
					this.campaignsLineChart.datasets[0].data = top_queries.map(query => query.total_searches);
				}
			} catch (error) {
				console.log(error)
			} finally {
				this.loading = false
			}
		},

		async getResponses() {
			this.loading = true
			try {
				const jurisdiction = this.client?.heygov_jurisdiction ?? this.client.url

				this.params = {}
				if (this.searchTerm && this.searchTerm.trim() !== '') {
					this.params.search = this.searchTerm.trim()
				}
				if (this.startDate && this.endDate) {
					this.params.start_date = this.startDate.trim()
					this.params.end_date = this.endDate.trim()
				} else {
					delete this.params.start_date
					delete this.params.end_date
				}

				const queryParams = {
					jurisdiction: jurisdiction,
					page: this.page,
					limit: this.perPage,
					...this.params,
				}

				const { data } = await axios.get(`${this.aiSearchAPIUrl}search/responses`, { params: queryParams })

				this.responses = data.data.data
				const records = data.data.meta.records
				const page = data.data.meta.page
				const page_size = data.data.meta.page_size

				this.totalItems = records
				this.page = page
				this.perPage = page_size

				this.$router.push({ query: { ...this.params, page: this.page } })
			} catch (error) {
				console.log(error)
			} finally {
				this.loading = false
			}
		},

		applyFilters() {
			this.page = 1
			this.getResponses()
		},

		clearFilters() {
			this.searchTerm = ''
			this.startDate = ''
			this.endDate = ''
			this.page = 1
			this.getResponses()
		},

		setPage(n) {
			this.page = n;
			this.getResponses();
		},

		dateDifferenceDays(date) {
			return differenceInDays(new Date(date), new Date())
		},

		showModal(response) {
			this.selectedResponse = response
			this.$responseModal.show()
		},
	},
}
</script>

<style scoped>
#clientAISearch {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.tableFixHead table {
	border-collapse: collapse;
	width: 100%;
}

.tableFixHead table th,
.tableFixHead table td {
	background: #fff;
}

.tableFixHead {
	overflow: auto;
	height: calc(100% - 50px);
}

.tableFixHead thead th {
	position: sticky;
	top: 0;
	background: #fff;
	z-index: 1;
}

.input-group-text {
	border: none;
}
</style>
